
import { defineComponent, reactive, onBeforeMount } from 'vue'
import { useRoute } from 'vue-router'

// @components
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'

// @module components
import CrmWhatsappFilters from '@/modules/CrmModule/components/CrmWhatsappFilters/index.vue'

// @types
import {
    GetWhatsappChatMessagesParams,
    GetWhatsappChatsParams,
    WhatsappMessage,
} from '@/core/types/notifications.types'

// @realisations
import { notifications_realisation } from '@/core/realisations/notifications.realisation'

// @composable
import useNotifications from '@/common/composable/useNotifications'

// @helpers
import cut from '@/common/helpers/cut'
import { useFile } from '@/common/composable/useFile'

const TABLE_COLUMNS = [
    {
        key: 'id',
        name: '№',
    },
    {
        key: 'phone',
        name: 'Номер телефона',
    },
    {
        key: 'message',
        name: 'Последнее сообщение',
        withHint: true,
    },
    {
        key: 'senderName',
        name: 'Кто последний ответил',
    },
    {
        key: 'sendedAt',
        name: 'Дата и время последнего сообщения',
    },
]

export default defineComponent({
    components: {
        NewTable,
        NewButton,
        Loader,
        NewLimit,
        NewPagination,
        CrmWhatsappFilters,
    },
    setup() {
        const route = useRoute()
        const notifications = useNotifications()
        const { saveFile } = useFile()

        const chatId = Number(route.params.chat_id)

        const state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as WhatsappMessage[],
            pagination: {
                page: 1,
                limit: 50,
                total_pages: 1,
                total_items: 20,
            },
            filters: {
                phoneNumber: null as string | null,
                createdAtFrom: null as string | null,
                createdAtTo: null as string | null,
                senderName: null as string | null,
            },
            excel: {
                phone: null as string | null,
            },
        })

        const loadHistory = async () => {
            try {
                state.is_loading = true

                const params: GetWhatsappChatMessagesParams = {
                    page: state.pagination.page,
                    limit: state.pagination.limit,
                    createdAtFrom: state.filters.createdAtFrom,
                    createdAtTo: state.filters.createdAtTo,
                    phone: state.filters.phoneNumber,
                    senderName: state.filters.senderName,
                    chatId,
                }

                const response = await notifications_realisation.getWhatsappChatMessages(params)
                state.list = response.data
                if (state.list.length) state.excel.phone = response.data[0].phone

                state.pagination.total_pages = response.meta.last_page
                state.pagination.total_items = response.meta.total

                state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении данных произошла ошибка' })
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(loadHistory)

        const onFiltersChanged = (filters: GetWhatsappChatsParams) => {
            state.filters.createdAtFrom = filters.createdAtFrom as string | null
            state.filters.createdAtTo = filters.createdAtTo as string | null
            state.filters.phoneNumber = filters.phoneNumber as string | null
            state.filters.senderName = filters.senderName as string | null

            loadHistory()
        }

        const onFiltersReset = () => {
            state.filters.createdAtFrom = null
            state.filters.createdAtTo = null
            state.filters.phoneNumber = null
            state.filters.senderName = null

            loadHistory()
        }

        const onLimitChange = (new_limit: number) => {
            state.pagination.limit = new_limit
            loadHistory()
        }

        const onChangePage = (new_page: number) => {
            state.pagination.page = new_page
            loadHistory()
        }

        const exportExcel = async () => {
            try {
                state.is_loading = true

                const params = {
                    phone: state.excel.phone,
                    createdAtFrom: state.filters.createdAtFrom,
                    createdAtTo: state.filters.createdAtTo,
                    senderName: state.filters.senderName,
                }
                const response = await notifications_realisation.exportExcelChat(params)
                saveFile(response, 'Whatsapp чат')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Ошибка при скачивании`,
                })
            } finally {
                state.is_loading = false
            }
        }

        return {
            TABLE_COLUMNS,
            state,
            loadHistory,
            onLimitChange,
            onChangePage,
            cut,
            onFiltersChanged,
            onFiltersReset,
            exportExcel,
        }
    },
})
